import React, { useState, useEffect } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { getSelectedRegistrationNumber } from '../../../../store/selectors/AuthSelectors';
import axios from '../../../../services/AxiosInstance';
import { getApiUrl } from '../../../../utils/ApiUtils';
import VatVerificationModal from './VatVerificationModal';
import moment from 'moment';

// Import new components
import VatModernHeader from './components/VatModernHeader';
import VatVerificationViewModal from './VatVerificationViewModal';
import { promptAndCloseHistoricalPeriod } from './closeHistoricalPeriod';
import VatProgressTracker from './components/VatProgressTracker';
import VatStepContent from './components/VatStepContent';
import VatStepNavigation from './components/VatStepNavigation';

import { Button } from 'react-bootstrap';
import {
    CheckCircle2,
    Info,
    Eye,
    Send,
    CreditCard,
    BookOpen
} from 'lucide-react';

//import { toast } from 'react-toastify';

import { toast } from 'sonner';

import { StepEnum } from './constants';

const stepTitles = {
    [StepEnum.INSTRUCTIONS]: "Instruktioner",
    [StepEnum.REVIEW]: "Granska Momsrapporten",
    [StepEnum.SUBMISSION]: "Skicka momsrapport",
    [StepEnum.PAYMENT]: "Betala momsen",
    [StepEnum.RECORD]: "Bokför dragning"
};

const stepOrder = [
    StepEnum.INSTRUCTIONS,
    StepEnum.REVIEW,
    StepEnum.SUBMISSION,
    StepEnum.PAYMENT,
    StepEnum.RECORD
];

const stepIcons = {
    [StepEnum.INSTRUCTIONS]: Info,
    [StepEnum.REVIEW]: Eye,
    [StepEnum.SUBMISSION]: Send,
    [StepEnum.PAYMENT]: CreditCard,
    [StepEnum.RECORD]: BookOpen
};

function VatPeriodStepper() {
    const { companyId, vatPeriodId, fiscalYearId } = useParams();
    const history = useHistory();
    // Display-only payment data. The value is never sent as declaration
    // authority; XML identity and all boxes are derived by the backend.

    // State management (matching current implementation)
    const [vatPeriod, setVatPeriod] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [undoingClose, setUndoingClose] = useState(false);
    const [currentStep, setCurrentStep] = useState(StepEnum.INSTRUCTIONS);
    const [viewingStep, setViewingStep] = useState(null); // null = viewing currentStep
    const [showVatModal, setShowVatModal] = useState(false);
    const [verifying, setVerifying] = useState(false);
    const [vatData, setVatData] = useState(null);
    const [totalVat, setTotalVat] = useState(null);
    const [transactions, setTransactions] = useState([]);
    const [totalDebit, setTotalDebit] = useState(0);
    const [totalCredit, setTotalCredit] = useState(0);
    const [operationInProgress, setOperationInProgress] = useState(false);

    // Verification tracking state
    const [reviewVerification, setReviewVerification] = useState(null);
    const [recordVerification, setRecordVerification] = useState(null);
    // Verifikat opened from the header chips, from any step.
    const [headerVerificationId, setHeaderVerificationId] = useState(null);
    const [paymentMadeDate, setPaymentMadeDate] = useState(null);
    const [showPaymentDateModal, setShowPaymentDateModal] = useState(false);
    const [verificationModalType, setVerificationModalType] = useState(null); // 'review' or 'record'
    const [pendingRecordDate, setPendingRecordDate] = useState(null);

    // Step completion tracking state
    const [completionStatus, setCompletionStatus] = useState({
        instructions_completed: false,
        review_completed: false,
        submission_completed: false,
        payment_completed: false,
        record_completed: false
    });

    const fetchVatPeriodDetails = async () => {
        try {
            const response = await axios.get(getApiUrl(`${companyId}/reporting/vat-period/${vatPeriodId}`), { withCredentials: true });
            if (response.data.success) {
                setVatPeriod(response.data.period);
                setCurrentStep(response.data.period.current_step);
                return response.data.period;
            } else {
                setError('Failed to load VAT period details');
            }
        } catch (err) {
            setError('Error fetching VAT period details');
        } finally {
            setLoading(false);
        }
        return null;
    };

    const fetchCompletionStatus = async () => {
        if (!vatPeriodId) return null;
        try {
            const statusResponse = await axios.get(
                getApiUrl(`${companyId}/reporting/vat-period/${vatPeriodId}/completion-status`),
                { withCredentials: true }
            );
            if (!statusResponse.data.success) return null;

            const status = statusResponse.data.status;
            setReviewVerification(status.review_verification || null);
            setRecordVerification(status.record_verification || null);
            setPaymentMadeDate(status.payment_date || null);
            setCompletionStatus({
                instructions_completed: !!status.instructions_completed,
                review_completed: !!status.review_completed,
                submission_completed: !!status.submission_completed,
                payment_completed: !!status.payment_completed,
                record_completed: !!status.record_completed
            });
            return status;
        } catch (statusError) {
            console.error('Error checking existing verifications:', statusError);
            return null;
        }
    };

    // Data fetching (identical to current implementation)
    useEffect(() => {
        fetchVatPeriodDetails();
    }, [companyId, vatPeriodId]);

    useEffect(() => {
        if (vatPeriod) {
            fetchVATCalculations();
        }
    }, [vatPeriod]);

    // Check for existing verifications when step changes or on mount
    useEffect(() => {
        if (vatPeriod) fetchCompletionStatus();
    }, [vatPeriod?.current_step, vatPeriodId, companyId]);

    const fetchVATCalculations = async (period = vatPeriod) => {
        if (!period) return;
        try {
            const getVATCalculationsApiUrl = getApiUrl(`${companyId}/reporting/vat-calculation`);
            const response = await axios.get(getVATCalculationsApiUrl, {
                withCredentials: true,
                params: {
                    start_date: period.start_date,
                    end_date: period.end_date,
                    fiscal_year_id: period.fiscal_year_id,
                },
            });

            if (response.data.success) {
                setVatData(response.data.vatData);
                setTotalVat(response.data.totalVat);
            } else {
                throw new Error(response.data.message);
            }
        } catch (error) {
            console.error('Error fetching VAT calculations:', error);
        }
    };

    // Business logic methods (identical to current implementation)
    const handleStepComplete = async (nextStep) => {
        setOperationInProgress(true);
        try {
            const validSteps = ['instructions', 'review', 'submission', 'payment', 'record'];

            if (!validSteps.includes(nextStep)) {
                throw new Error('Invalid step value');
            }

            // Special handling for submission step - generate report
            // This is an intention-only request. The backend derives company
            // identity, period, boxes and total from authoritative state.
            if (nextStep === StepEnum.SUBMISSION) {
                const generateReportResponse = await axios.post(
                    getApiUrl(`${companyId}/reporting/vat-period/${vatPeriodId}/generate-report`),
                    {},
                    { withCredentials: true }
                );
                if (!generateReportResponse.data.success) {
                    throw new Error('Failed to generate VAT report');
                }
            }

            await axios.post(
                getApiUrl(`${companyId}/reporting/vat-period/${vatPeriodId}/update-step`),
                { current_step: nextStep },
                { withCredentials: true }
            );

            // Mark the step we're leaving as completed locally
            const previousStepIndex = stepOrder.indexOf(nextStep) - 1;
            if (previousStepIndex >= 0) {
                const previousStep = stepOrder[previousStepIndex];
                setCompletionStatus(prev => ({
                    ...prev,
                    [`${previousStep}_completed`]: true
                }));
            }

            if (nextStep === StepEnum.SUBMISSION) {
                // Generation freezes the period and creates the attachment.
                // Refresh before rendering submission so the download link
                // and frozen-state navigation use the committed server row.
                await fetchVatPeriodDetails();
            } else {
                setCurrentStep(nextStep);
            }

        } catch (err) {
            const message = err.response?.data?.message || err.message || 'Error updating current step';
            // Show user-friendly message for common errors
            if (message.includes('must be closed') || message.includes('måste stängas')) {
                toast.error('Föregående momsperioder måste stängas innan du kan fortsätta. Gå till momsperiodlistan och stäng äldre perioder först.');
            } else {
                toast.error(message);
            }
            console.error('Error in handleStepComplete:', message);
        } finally {
            setOperationInProgress(false);
        }
    };

    const handleCompletePeriod = async () => {
        setOperationInProgress(true);
        setError(null);

        try {
            const response = await axios.post(
                getApiUrl(`${companyId}/reporting/vat-period/${vatPeriodId}/close`),
                { fiscalYearId: vatPeriod.fiscal_year_id },
                { withCredentials: true }
            );

            if (response.data.success) {
                setVatPeriod(prevState => ({ ...prevState, state: 'closed' }));
                // Refresh period data to get updated state
                const periodResponse = await axios.get(
                    getApiUrl(`${companyId}/reporting/vat-period/${vatPeriodId}`),
                    { withCredentials: true }
                );
                if (periodResponse.data.success) {
                    setVatPeriod(periodResponse.data.period);
                }
            } else {
                setError('Failed to close VAT period');
            }
        } catch (err) {
            setError('Error closing VAT period');
            console.error('Error in handleCompletePeriod:', err);
        } finally {
            setOperationInProgress(false);
        }
    };

    const handleVatPeriodClose = async () => {
        setError(null);
        // Same dialog and same recording as the periods list. This button used to
        // post only the fiscal year, so a period closed from inside the wizard
        // kept vat_total 0 and no note — and any refusal became a generic toast.
        const result = await promptAndCloseHistoricalPeriod({
            companyId,
            period: {
                vat_reporting_period_id: vatPeriodId,
                start_date: vatPeriod.start_date,
                end_date: vatPeriod.end_date,
                fiscal_year_id: vatPeriod.fiscal_year_id,
            },
        });

        if (result.error) {
            setError(result.error);
            toast.error(result.error);
            return;
        }
        if (!result.closed) return;

        setVatPeriod(prevState => ({ ...prevState, state: 'closed', current_step: 'record', handled: 1 }));
        setCurrentStep(StepEnum.RECORD);
        toast.success('Momsperioden har stängts', {
            description: 'Perioden är nu markerad som avslutad'
        });
    };

    const handleUndoClose = async () => {
        setUndoingClose(true);
        setError(null);

        try {
            const response = await axios.post(
                getApiUrl(`${companyId}/reporting/vat-period/${vatPeriodId}/undo-close`),
                {},
                { withCredentials: true }
            );

            if (response.data.success) {
                // Reopen invalidates the attachment, verification references,
                // payment evidence and completion flags server-side. Hydrate
                // every dependent browser view from that committed state.
                const freshPeriod = await fetchVatPeriodDetails();
                await Promise.all([
                    fetchCompletionStatus(),
                    fetchVATCalculations(freshPeriod),
                ]);
                setViewingStep(null);
            } else {
                setError('Failed to reopen VAT period');
            }
        } catch (err) {
            setError('Error reopening VAT period');
        } finally {
            setUndoingClose(false);
        }
    };

    // The settlement verifikat is composed by the server from the period row
    // and the declared ruta 49 (G39). The client sends no dates, no fiscal
    // year and no account list, and the legs it displays are the legs that
    // will be posted — it confirms a proposal, it does not build one.
    const handleReviewVatPeriod = () => {
        const settlementProposalUrl = getApiUrl(
            `${companyId}/reporting/vat-period/${vatPeriodId}/settlement-verification`
        );
        axios.get(settlementProposalUrl, { withCredentials: true })
            .then((response) => {
                const proposal = response.data.proposal;
                setTransactions(proposal.transactions);
                calculateTotals(proposal.transactions);
                setVerificationModalType('review');
                setShowVatModal(true);
            })
            .catch((error) => {
                console.error('Error fetching VAT settlement proposal:', error);
                toast.error(
                    error.response?.data?.message
                    || 'Kunde inte ta fram underlaget för momsredovisningsverifikatet'
                );
            });
    };

    const calculateTotals = (transactions) => {
        const totalDebit = transactions.reduce((acc, transaction) => acc + parseFloat(transaction.debit || 0), 0);
        const totalCredit = transactions.reduce((acc, transaction) => acc + parseFloat(transaction.credit || 0), 0);
        setTotalDebit(totalDebit);
        setTotalCredit(totalCredit);
    };

    const handleConfirmVatVerification = async () => {
        setVerifying(true);
        setError(null);

        try {
            // Date, description and legs all come from the server — posting
            // them back would reintroduce the client-authored verifikat.
            const settlementUrl = getApiUrl(
                `${companyId}/reporting/vat-period/${vatPeriodId}/settlement-verification`
            );
            const response = await axios.post(settlementUrl, {}, { withCredentials: true });

            if (response.data.success) {
                const declared = response.data.proposal?.declared_vat;
                // Snapshot the declared VAT locally before accounts are zeroed.
                // The backend stores it when generating XML; the header needs
                // it immediately.
                setVatPeriod(prev => prev
                    ? { ...prev, vat_total: declared !== undefined ? declared : totalVat }
                    : prev);

                const created = response.data.verification || {};
                setReviewVerification({
                    verification_id: created.verificationId || created.verification_id,
                    series: created.series,
                    number: created.verificationNumber || created.number
                });

                setShowVatModal(false);
                setTransactions([]);

                // Mark review step as completed
                await markStepComplete('review');

                toast.success("Verifikat skapat och kopplat till momsperioden");
            } else {
                throw new Error('Failed to create verification');
            }
        } catch (error) {
            setError(error.response?.data?.message || 'Ett fel uppstod vid skapandet av verifikatet');
            console.error('Error in handleConfirmVatVerification:', error);
        } finally {
            setVerifying(false);
        }
    };

    const handleDeleteVerification = async (verificationId, step) => {
        const swal = (await import('sweetalert')).default;
        const confirmDelete = await swal({
            title: "Ångra verifikat?",
            text: "Ett återföringsverifikat kommer att skapas som reverserar det ursprungliga verifikatet.",
            icon: "warning",
            buttons: {
                cancel: {
                    text: "Avbryt",
                    value: false,
                    visible: true
                },
                confirm: {
                    text: "Ångra verifikat",
                    value: true,
                    visible: true,
                    className: "btn-danger"
                }
            },
            dangerMode: true
        });

        if (!confirmDelete) return;

        setOperationInProgress(true);

        try {
            const response = await axios.delete(
                getApiUrl(`${companyId}/reporting/vat-period/${vatPeriodId}/verification/${verificationId}/${step}`),
                { withCredentials: true }
            );

            if (response.data.success) {
                // Clear the verification from state
                if (step === 'review') {
                    setReviewVerification(null);
                    // Unmark review step as completed
                    await axios.delete(
                        getApiUrl(`${companyId}/reporting/vat-period/${vatPeriodId}/complete-step/review`),
                        { withCredentials: true }
                    );
                    setCompletionStatus(prev => ({ ...prev, review_completed: false }));
                } else if (step === 'record') {
                    setRecordVerification(null);
                    // Unmark record step as completed
                    await axios.delete(
                        getApiUrl(`${companyId}/reporting/vat-period/${vatPeriodId}/complete-step/record`),
                        { withCredentials: true }
                    );
                    setCompletionStatus(prev => ({ ...prev, record_completed: false }));
                }

                toast.success("Återföringsverifikat skapat");
            }
        } catch (error) {
            const message = error.response?.data?.message || 'Kunde inte ångra verifikat';
            toast.error(message);
        } finally {
            setOperationInProgress(false);
        }
    };

    const handlePaymentMade = async (paymentDate) => {
        setOperationInProgress(true);

        try {
            const response = await axios.post(
                getApiUrl(`${companyId}/reporting/vat-period/${vatPeriodId}/payment-made`),
                { payment_date: paymentDate },
                { withCredentials: true }
            );

            if (response.data.success) {
                setShowPaymentDateModal(false);
                // The one backend transition persists both payment evidence
                // and completion. Re-hydrate instead of issuing a second POST
                // that could fail after the date had already committed.
                await fetchCompletionStatus();

                //const swal = (await import('sweetalert')).default;
                //swal("Framgång", "Betalning registrerad", "success");
                toast.success("Betalning registrerad");
            }
        } catch (error) {
            //const swal = (await import('sweetalert')).default;
            //swal("Fel", "Kunde inte registrera betalning", "error");
            toast.error("Kunde inte registrera betalning");
        } finally {
            setOperationInProgress(false);
        }
    };

    // Payment verification creation removed - users handle payment outside VAT flow
    // Future: Automated agents will create payment verifications automatically

    const handleClearPayment = async () => {
        const swal = (await import('sweetalert')).default;
        const confirmClear = await swal({
            title: "Ångra betalning?",
            text: "Är du säker på att du vill ta bort betalningsdatumet?",
            icon: "warning",
            buttons: {
                cancel: {
                    text: "Avbryt",
                    value: false,
                    visible: true
                },
                confirm: {
                    text: "Ta bort",
                    value: true,
                    visible: true,
                    className: "btn-danger"
                }
            },
            dangerMode: true
        });

        if (!confirmClear) return;

        setOperationInProgress(true);

        try {
            const response = await axios.delete(
                getApiUrl(`${companyId}/reporting/vat-period/${vatPeriodId}/payment-made`),
                { withCredentials: true }
            );

            if (response.data.success) {
                setPaymentMadeDate(null);
                // The backend clears the date and completion marker together
                // under the VAT-period lock, so reopen cannot interleave.
                setCompletionStatus(prev => ({ ...prev, payment_completed: false }));

                //swal("Framgång", "Betalning borttagen", "success");
                toast.success("Betalning borttagen");
            }
        } catch (error) {
            const message = error.response?.data?.message || 'Kunde inte ta bort betalning';
            //swal("Fel", message, "error");
            toast.error(message);
        } finally {
            setOperationInProgress(false);
        }
    };

    // The skattekonto verifikat is likewise composed by the server, which
    // resolves the chart roles and — on a refund — settles the receivable the
    // review step created rather than the settlement account (G38).
    const handleCreateRecordVerification = async (recordDate) => {
        setOperationInProgress(true);
        try {
            const recordProposalUrl = getApiUrl(
                `${companyId}/reporting/vat-period/${vatPeriodId}/record-verification`
            );
            const response = await axios.get(recordProposalUrl, {
                params: recordDate ? { record_date: recordDate } : {},
                withCredentials: true
            });
            const proposal = response.data.proposal;

            setTransactions(proposal.transactions);
            setTotalDebit(proposal.totals.debit);
            setTotalCredit(proposal.totals.credit);
            setVerificationModalType('record');
            setPendingRecordDate(proposal.date);
            setShowVatModal(true);
        } catch (error) {
            console.error('Error fetching VAT record proposal:', error);
            toast.error(
                error.response?.data?.message || 'Kunde inte ta fram underlaget för skattekontoverifikatet'
            );
        } finally {
            setOperationInProgress(false);
        }
    };

    const handleConfirmRecordVerification = async () => {
        setVerifying(true);
        setError(null);

        try {
            // The booking date is the only thing the user contributes; the
            // legs are rebuilt server-side from the frozen declaration.
            const recordUrl = getApiUrl(
                `${companyId}/reporting/vat-period/${vatPeriodId}/record-verification`
            );
            const response = await axios.post(
                recordUrl,
                { record_date: pendingRecordDate || undefined },
                { withCredentials: true }
            );

            if (response.data.success) {
                const created = response.data.verification || {};
                setRecordVerification({
                    verification_id: created.verificationId || created.verification_id,
                    series: created.series,
                    number: created.verificationNumber || created.number
                });

                setShowVatModal(false);
                setTransactions([]);

                // Mark record step as completed (this also closes the period)
                await markStepComplete('record');

                // Reload VAT period data to get updated state
                const periodResponse = await axios.get(
                    getApiUrl(`${companyId}/reporting/vat-period/${vatPeriodId}`),
                    { withCredentials: true }
                );
                if (periodResponse.data.success) {
                    setVatPeriod(periodResponse.data.period);
                }

                toast.success("Verifikat för dragning skapat och momsperiod slutförd");
            } else {
                setError('Kunde inte skapa verifikatet');
            }
        } catch (error) {
            setError('Ett fel uppstod vid skapandet av verifikatet');
            console.error('Error in handleConfirmRecordVerification:', error);
            toast.error(error.response?.data?.message || 'Ett fel uppstod vid skapandet av verifikatet');
        } finally {
            setVerifying(false);
        }
    };

    // Step completion marking
    const markStepComplete = async (step) => {
        try {
            const response = await axios.post(
                getApiUrl(`${companyId}/reporting/vat-period/${vatPeriodId}/complete-step/${step}`),
                {},
                { withCredentials: true }
            );

            if (response.data.success) {
                setCompletionStatus(prev => ({
                    ...prev,
                    [`${step}_completed`]: true
                }));
                toast.success(`Steg "${step}" markerat som klart`);
            }
        } catch (error) {
            toast.error(error.response?.data?.message || 'Kunde inte markera steg som klart');
        }
    };

    // Step navigation helpers
    const isStepCompleted = (step) => {
        return completionStatus[`${step}_completed`] || false;
    };

    const isStepActive = (step) => {
        return step === currentStep;
    };

    const getCurrentStepIndex = () => {
        return stepOrder.indexOf(currentStep);
    };

    // The furthest step the user has reached (for enabling preview clicks)
    const getFurthestStepIndex = () => {
        for (let i = stepOrder.length - 1; i >= 0; i--) {
            if (completionStatus[`${stepOrder[i]}_completed`]) {
                return i + 1; // Can access the step after the last completed one
            }
        }
        return 0; // Only first step accessible
    };

    const canGoToNextStep = () => {
        const currentIndex = getCurrentStepIndex();

        if (currentIndex >= stepOrder.length - 1) {
            return false;
        }

        // Instructions step is informational — always allow proceeding
        if (currentStep === StepEnum.INSTRUCTIONS) {
            return true;
        }

        // Review step — requires zeroing verification before proceeding
        // This is the industry standard: you must book the momsredovisning before submitting to Skatteverket
        if (currentStep === StepEnum.REVIEW) {
            return !!reviewVerification;
        }

        // Other steps require completion
        return completionStatus[`${currentStep}_completed`] || false;
    };

    const canGoToPreviousStep = () => {
        const currentIndex = getCurrentStepIndex();
        if (currentIndex <= 0) return false;

        // Cannot go back if current step has irreversible actions taken
        if (currentStep === StepEnum.REVIEW && reviewVerification) return false;
        if (currentStep === StepEnum.PAYMENT && paymentMadeDate) return false;
        if (currentStep === StepEnum.RECORD && recordVerification) return false;

        return true;
    };

    const handleStepChange = async (targetStep, direction = 'forward') => {
        setViewingStep(null); // Clear any preview
        if (direction === 'forward') {
            await handleStepComplete(targetStep);
        } else {
            // Persist backward navigation to backend so refresh returns to correct step
            try {
                await axios.post(
                    getApiUrl(`${companyId}/reporting/vat-period/${vatPeriodId}/update-step`),
                    { current_step: targetStep },
                    { withCredentials: true }
                );
            } catch (err) {
                console.error('Error persisting backward navigation:', err);
            }
            setCurrentStep(targetStep);
        }
    };

    // Handle click on progress tracker — preview mode (no actual navigation)
    const handleStepClick = (step) => {
        if (step === currentStep) {
            setViewingStep(null); // Return to actual step
        } else {
            setViewingStep(step); // Preview any step
        }
    };

    // The step whose content is currently displayed
    const displayedStep = viewingStep || currentStep;
    const isPreviewing = viewingStep !== null && viewingStep !== currentStep;

    const goToNextStep = () => {
        if (!canGoToNextStep()) {
            // Show appropriate message based on current step
            if (currentStep === StepEnum.REVIEW && !reviewVerification) {
                toast.error("Du måste skapa ett verifikat för momsrapporten innan du fortsätter");
            } else if (currentStep === StepEnum.RECORD && !recordVerification) {
                toast.error("Du måste skapa ett verifikat för dragningen innan du fortsätter");
            }
            return;
        }

        const nextStepIndex = getCurrentStepIndex() + 1;
        const nextStep = stepOrder[nextStepIndex];
        handleStepComplete(nextStep);
    };

    const goToPreviousStep = () => {
        if (!canGoToPreviousStep()) {
            // Show appropriate message based on current step
            if (currentStep === StepEnum.REVIEW && reviewVerification) {
                toast.error("Du måste ta bort verifikatet innan du kan gå tillbaka");
            } else if (currentStep === StepEnum.PAYMENT && paymentMadeDate) {
                toast.error("Du måste ångra betalningsdatumet innan du kan gå tillbaka");
            } else if (currentStep === StepEnum.RECORD && recordVerification) {
                toast.error("Du måste ta bort verifikatet innan du kan gå tillbaka");
            }
            return;
        }

        const previousStepIndex = getCurrentStepIndex() - 1;
        const previousStep = stepOrder[previousStepIndex];
        setCurrentStep(previousStep);
    };

    // Common props to pass to all step components
    const stepProps = {
        vatPeriod,
        currentStep,
        vatData,
        totalVat,
        transactions,
        totalDebit,
        totalCredit,
        companyId,
        vatPeriodId,
        fiscalYearId,
        isPreviewMode: isPreviewing,
        loading,
        error,
        verifying,
        undoingClose,
        handleStepComplete,
        handleVatPeriodClose,
        handleCompletePeriod,
        handleReviewVatPeriod,
        setShowVatModal,
        setTransactions,
        setTotalDebit,
        setTotalCredit,
        setError,
        setVerifying,
        goToNextStep,
        goToPreviousStep,
        canGoToNextStep,
        canGoToPreviousStep,
        calculateTotals,
        // Verification tracking props
        reviewVerification,
        recordVerification,
        paymentMadeDate,
        handleDeleteVerification,
        handlePaymentMade,
        handleClearPayment,
        handleCreateRecordVerification,
        operationInProgress,
        // Step completion tracking props
        markStepComplete,
        completionStatus,
        onReportGenerated: fetchVatPeriodDetails
    };

    if (loading) {
        return (
            <div
                className="d-flex justify-content-center align-items-center"
                style={{
                    height: '400px',
                    background: '#f8fafc',
                    borderRadius: '12px',
                    margin: '2rem 0'
                }}
            >
                <div style={{ textAlign: 'center' }}>
                    <div
                        className="spinner-border text-primary mb-3"
                        role="status"
                        style={{ width: '3rem', height: '3rem' }}
                    >
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="text-muted">Laddar momsperiod...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div
                className="alert alert-danger"
                role="alert"
                style={{
                    borderRadius: '12px',
                    border: '1px solid #f5c6cb',
                    padding: '2rem',
                    margin: '2rem 0'
                }}
            >
                <h4 className="alert-heading">Ett fel uppstod</h4>
                <p className="mb-0">{error}</p>
            </div>
        );
    }

    return (
        <>
            <style>{`
                .vat-stepper-container {
                    max-width: 1200px;
                    margin: 0 auto;
                    padding: 0 1rem;
                }
                
                .vat-content-wrapper {
                    background: #f8fafc;
                    min-height: 100vh;
                    padding: 2rem 0;
                }
                
                /* Custom scrollbar for the container */
                .vat-stepper-container::-webkit-scrollbar {
                    width: 6px;
                }
                
                .vat-stepper-container::-webkit-scrollbar-track {
                    background: #f1f1f1;
                    border-radius: 3px;
                }
                
                .vat-stepper-container::-webkit-scrollbar-thumb {
                    background: #c1c1c1;
                    border-radius: 3px;
                }
                
                .vat-stepper-container::-webkit-scrollbar-thumb:hover {
                    background: #a8a8a8;
                }
                
                /* Mobile responsive adjustments */
                @media (max-width: 768px) {
                    .vat-stepper-container {
                        padding: 0 0.5rem;
                    }
                    
                    .vat-content-wrapper {
                        padding: 1rem 0;
                    }
                }
            `}</style>

            <div className="">
                <div className="vat-stepper-container">
                    <VatModernHeader
                        verifications={[
                            reviewVerification && { ...reviewVerification, label: 'Momsredovisning' },
                            recordVerification && { ...recordVerification, label: 'Dragning från skattekontot' },
                        ].filter(Boolean)}
                        onVerificationClick={(v) => setHeaderVerificationId(v.verification_id)}
                        vatPeriod={vatPeriod}
                        totalVat={vatPeriod?.handled ? null : totalVat}
                        undoingClose={undoingClose}
                        handleUndoClose={handleUndoClose}
                        handleVatPeriodClose={handleVatPeriodClose}
                        loading={loading}
                        currentStep={currentStep}
                    />

                    {vatPeriod?.handled ? (
                        <div style={{
                            background: 'white',
                            border: '1px solid #e2e8f0',
                            borderRadius: '8px',
                            padding: '2rem',
                            textAlign: 'center',
                            color: 'var(--secondary)'
                        }}>
                            <CheckCircle2 size={48} style={{ color: 'var(--primary)', marginBottom: '1rem' }} />
                            <h4 style={{ color: '#1e293b', marginBottom: '0.5rem' }}>Perioden är markerad som redan hanterad</h4>
                            <p style={{ marginBottom: '1.5rem' }}>
                                Momsen för denna period har hanterats utanför systemet. Inga ytterligare åtgärder behövs.
                            </p>
                            <Button
                                variant="outline-secondary"
                                onClick={() => history.push(`/${companyId}/vat-periods`)}
                            >
                                Tillbaka till momsperioder
                            </Button>
                        </div>
                    ) : (
                        <>
                            <VatProgressTracker
                                currentStep={displayedStep}
                                actualCurrentStep={currentStep}
                                stepOrder={stepOrder}
                                stepTitles={stepTitles}
                                stepIcons={stepIcons}
                                isStepCompleted={isStepCompleted}
                                isStepActive={(step) => step === displayedStep}
                                onStepClick={handleStepClick}
                            />

                            {isPreviewing && (
                                <div
                                    className="d-flex align-items-center justify-content-between px-3 py-2 mb-2"
                                    style={{ background: 'var(--rgba-primary-1)', border: '1px solid var(--rgba-primary-3)', borderRadius: '6px', fontSize: '0.85rem' }}
                                >
                                    <span style={{ color: 'var(--primary)' }}>
                                        Förhandsvisning av <strong>{stepTitles[viewingStep]}</strong>
                                    </span>
                                    <button
                                        onClick={() => setViewingStep(null)}
                                        style={{ background: 'none', border: 'none', color: 'var(--primary)', cursor: 'pointer', fontWeight: 500, fontSize: '0.85rem' }}
                                    >
                                        ← Tillbaka till {stepTitles[currentStep]}
                                    </button>
                                </div>
                            )}

                            <VatStepContent
                                currentStep={displayedStep}
                                stepProps={stepProps}
                            />

                            <VatStepNavigation
                                currentStep={currentStep}
                                stepOrder={stepOrder}
                                onStepChange={handleStepChange}
                                onCompletePeriod={handleCompletePeriod}
                                operationInProgress={operationInProgress}
                                canProceedToNext={canGoToNextStep()}
                                canGoBack={canGoToPreviousStep()}
                                vatPeriod={vatPeriod}
                                recordVerification={recordVerification}
                            />
                        </>
                    )}
                </div>
            </div>

            {/* Keep existing modals */}
            <VatVerificationModal
                showModal={showVatModal}
                setShowModal={setShowVatModal}
                transactions={transactions}
                totalDebit={totalDebit}
                totalCredit={totalCredit}
                verifying={verifying}
                error={error}
                handleConfirmVerification={verificationModalType === 'record' ? handleConfirmRecordVerification : handleConfirmVatVerification}
                vatPeriod={vatPeriod}
                modalType={verificationModalType}
                verificationDescription={
                    verificationModalType === 'record'
                        ? `Skattekontouttag moms ${vatPeriod ? moment(vatPeriod.start_date).format('MMM YYYY') : ''}`
                        : `Momsredovisning ${vatPeriod ? moment(vatPeriod.start_date).format('MMM') + '–' + moment(vatPeriod.end_date).format('MMM YYYY') : ''}`
                }
            />

            {/* Opened from the header's verifikat chips. */}
            <VatVerificationViewModal
                show={!!headerVerificationId}
                onHide={() => setHeaderVerificationId(null)}
                verificationId={headerVerificationId}
            />
        </>
    );
}

export default VatPeriodStepper;
